// src/routes/Franchises.tsx
import { useMutation, useQuery } from 'convex/react';
import { useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

export default function Franchises() {
  const franchises = useQuery(api.franchises.listFranchises);
  const me = useQuery(api.franchises.myFranchise);
  const claim = useMutation(api.franchises.claimFranchise);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Id<'franchises'> | null>(null);

  if (franchises === undefined || me === undefined) {
    return <div className="p-6">Loading…</div>;
  }
  if (franchises.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">Franchises</h1>
        <p className="mt-4 text-gray-600">
          The league has not been seeded yet. The commissioner must run
          <code className="mx-1 rounded bg-gray-100 px-1">seedLeague</code>
          from the{' '}
          <a href="/commish" className="underline">
            /commish
          </a>{' '}
          page.
        </p>
      </div>
    );
  }

  async function handleClaim(franchiseId: Id<'franchises'>) {
    setError(null);
    setPending(franchiseId);
    try {
      await claim({ franchiseId });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold">Franchises</h1>
      {me ? (
        <p className="mt-2 text-sm text-gray-600">
          You own <strong>{me.name}</strong>.
        </p>
      ) : (
        <p className="mt-2 text-sm text-gray-600">
          Pick the franchise you'd like to own. You can claim only one.
        </p>
      )}
      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <table className="mt-4 w-full border-collapse">
        <thead>
          <tr className="border-b text-left text-sm text-gray-500">
            <th className="py-2">Name</th>
            <th>Division</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {franchises.map((f) => {
            const claimedByMe = me?._id === f._id;
            const claimedBySomeone = !!f.clerkUserId && !claimedByMe;
            return (
              <tr key={f._id} className="border-b">
                <td className="py-2 font-medium">{f.name}</td>
                <td>{f.division}</td>
                <td>{claimedByMe ? 'You' : claimedBySomeone ? 'Claimed' : 'Unclaimed'}</td>
                <td className="text-right">
                  {!me && !claimedBySomeone && (
                    <button
                      type="button"
                      onClick={() => handleClaim(f._id)}
                      disabled={pending === f._id}
                      className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                    >
                      {pending === f._id ? 'Claiming…' : 'Claim'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
